import { Injectable } from '@angular/core';
import { analyticsConfig } from '../config/analytics.config';

/**
 * Servicio de analytics unificado con Google Tag Manager.
 * Todos los eventos se envían al dataLayer para que GTM los procese.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly projectParam = 'criptococos_wallet';
  private readonly defaultTipoZona = 'CriptoCocos';
  private userId: string | null = null;

  private canTrack(): boolean {
    return analyticsConfig.enabled;
  }

  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  trackPageView(params: {
    tipoZona?: string;
    zona: string;
    subZona?: string;
    seccion?: string;
    subSeccion?: string;
    pageTitle?: string;
    pageLocation?: string;
    pagePath?: string;
    userId?: string | null;
    monto?: number;
    comision?: number;
    cic?: number | string;
    tipo_cambio?: number;
    moneda_transaccion?: string;
  }): void {
    if (!this.canTrack()) {
      return;
    }

    const basePayload = {
      event: 'page_view',
      tipoEvento: 'Visualización',
      tipoZona: params.tipoZona ?? this.defaultTipoZona,
      zona: params.zona,
      subZona: params.subZona ?? 'none',
      seccion: params.seccion ?? 'none',
      subSeccion: params.subSeccion ?? 'none',
      ...this.getPageContext(params.pageTitle, params.pageLocation, params.pagePath, params.userId),
    };

    const transactionFields: Record<string, unknown> = {};
    if (params['monto'] !== undefined) transactionFields['monto'] = params['monto'];
    if (params['comision'] !== undefined) transactionFields['comision'] = params['comision'];
    if (params['cic'] !== undefined) transactionFields['cic'] = params['cic'];
    if (params['tipo_cambio'] !== undefined) transactionFields['tipo_cambio'] = params['tipo_cambio'];
    if (params['moneda_transaccion'] !== undefined) transactionFields['moneda_transaccion'] = params['moneda_transaccion'];

    const payload = {
      ...basePayload,
      ...transactionFields,
    };

    this.pushToDataLayer(payload);
  }

  trackVirtualEvent(params: {
    tipoElemento: string;
    tipoEvento?: 'Interacción' | 'Visualización';
    tipoZona?: string;
    zona: string;
    subZona?: string;
    seccion?: string;
    subSeccion?: string;
    cta: string;
    pageTitle?: string;
    pageLocation?: string;
    pagePath?: string;
    userId?: string | null;
    moneda_transaccion?: string;
    monto?: number;
    comision?: number;
    cic?: number | string;
    tipo_cambio?: number;
  }): void {
    if (!this.canTrack()) {
      return;
    }

    const basePayload = {
      event: 'virtualEvent',
      tipoEvento: params.tipoEvento ?? 'Interacción',
      tipoElemento: params.tipoElemento,
      tipoZona: params.tipoZona ?? this.defaultTipoZona,
      zona: params.zona,
      subZona: params.subZona ?? 'none',
      seccion: params.seccion ?? 'none',
      subSeccion: params.subSeccion ?? 'none',
      page_location: this.resolvePageLocation(params.pageLocation),
      page_title: this.resolvePageTitle(params.pageTitle),
      page_path: this.resolvePagePath(params.pagePath),
      CTA: params.cta,
      user_id: this.resolveUserId(params.userId),
    };
    const extraFields: Record<string, unknown> = {};
    if (params.moneda_transaccion !== undefined) extraFields['moneda_transaccion'] = params.moneda_transaccion;
    if (params.monto !== undefined) extraFields['monto'] = params.monto;
    if (params.comision !== undefined) extraFields['comision'] = params.comision;
    if (params.cic !== undefined) extraFields['cic'] = params.cic;
    if (params.tipo_cambio !== undefined) extraFields['tipo_cambio'] = params.tipo_cambio;
    const payload = { ...basePayload, ...extraFields };

    this.pushToDataLayer(payload);
  }

  trackEvent(action: string, category: string, label?: string, value?: number): void {
    if (!this.canTrack()) {
      return;
    }

    const payload: Record<string, unknown> = {
      event: action,
      event_category: category,
      event_label: label,
      value,
      custom_parameter_1: this.projectParam,
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    };

    if (analyticsConfig.debug) {
      console.log('[Analytics Debug][event]', action, payload);
    }

    this.pushToDataLayer(payload);
  }

  trackPage(pageName: string, pageTitle?: string): void {
    if (!this.canTrack()) {
      return;
    }

    const payload = {
      event: 'page_view',
      page_title: pageTitle || pageName,
      page_location: window.location.href,
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    };

    if (analyticsConfig.debug) {
      console.log('[Analytics Debug][page_view]', payload);
    }

    this.pushToDataLayer(payload);
  }

  trackConversion(conversionName: string, conversionValue?: number): void {
    if (!this.canTrack()) {
      return;
    }

    const payload = {
      event: 'conversion',
      send_to: analyticsConfig.measurementId,
      event_category: 'conversion',
      event_label: conversionName,
      value: conversionValue ?? 1,
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    };

    if (analyticsConfig.debug) {
      console.log('[Analytics Debug][conversion]', payload);
    }

    this.pushToDataLayer(payload);
  }

  trackFormError(errorType: string, errorMessage: string, fieldName: string): void {
    if (!this.canTrack()) {
      return;
    }

    this.pushToDataLayer({
      event: 'form_error',
      event_category: 'form',
      event_label: `${fieldName}: ${errorType}`,
      error_message: errorMessage,
      field_name: fieldName,
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    });
  }

  trackFormProgress(step: number, totalSteps: number): void {
    if (!this.canTrack()) {
      return;
    }

    this.pushToDataLayer({
      event: 'form_progress',
      event_category: 'form',
      event_label: `step_${step}_of_${totalSteps}`,
      form_step: step,
      form_total_steps: totalSteps,
      progress_percentage: Math.round((step / totalSteps) * 100),
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    });
  }

  trackTimeOnSection(section: string, timeSpentSeconds: number): void {
    if (!this.canTrack()) {
      return;
    }

    this.pushToDataLayer({
      event: 'timing_complete',
      name: `time_on_${section}`,
      value: timeSpentSeconds,
      event_category: 'engagement',
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    });
  }

  trackScrollDepth(percentage: number): void {
    if (!this.canTrack()) {
      return;
    }

    this.pushToDataLayer({
      event: 'scroll',
      event_category: 'engagement',
      event_label: `${percentage}%`,
      scroll_depth: percentage,
      environment: analyticsConfig.environmentName,
      debug_mode: analyticsConfig.debug
    });
  }

  private pushToDataLayer(payload: Record<string, unknown>): void {
    const dataLayer = ((window as any).dataLayer ?? []) as Record<string, unknown>[];
    (window as any).dataLayer = dataLayer;
    dataLayer.push(payload);

    if (analyticsConfig.debug) {
      console.log('[Analytics Debug][dataLayer]', payload);
    }
  }

  private getPageContext(
    pageTitle?: string,
    pageLocation?: string,
    pagePath?: string,
    userId?: string | null
  ) {
    return {
      page_location: this.resolvePageLocation(pageLocation),
      page_title: this.resolvePageTitle(pageTitle),
      page_path: this.resolvePagePath(pagePath),
      user_id: this.resolveUserId(userId),
    };
  }

  private resolvePageTitle(pageTitle?: string): string {
    return pageTitle ?? document.title ?? '';
  }

  private resolvePageLocation(pageLocation?: string): string {
    return pageLocation ?? window.location.href;
  }

  private resolvePagePath(pagePath?: string): string {
    return pagePath ?? window.location.pathname;
  }

  private resolveUserId(userId?: string | null): string | null {
    if (typeof userId !== 'undefined') {
      return userId;
    }
    return this.userId;
  }
}
